import React from "react";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { GRID_ACTION_TYPES, GridActionType } from "../../../../assets/types";
import {
  ClaimHelper,
  isGridActionType,
} from "../../../../assets/utils/helpers";
import { Constants } from "../../../../assets/utils/lib/constants";
import { useSnackbar } from "../../../../assets/vfx/snackbar/vfxSnackbarContext";
import { MessageSeverity } from "../../../../assets/utils/lib/messageSeverity";
import { apiErrorMessage } from "../../../../assets/utils/lib/error";
import { Dialog, LoadingIndicator } from "../../../../assets/vfx";
import { CategoryForm } from "./categoryForm";
import { CommunityCategoryDto } from "../../../../services/dto";
import { CategoryService } from "./categoryService";
import { CategoryView } from "./categoryView";
import { CategoryPage } from "./category";
import { useCommunity } from "../../../../context/communityContext";

export const CategoryContainer = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const { communityId: routeCommunityId } = useParams<{
    communityId?: string;
  }>();
  const { communityId: contextCommunityId } = useCommunity();

  const claimCommunityId = (!routeCommunityId
    ? ClaimHelper.getCommunityId()
    : undefined) || undefined;
  const resolvedCommunityId = routeCommunityId ?? claimCommunityId ?? contextCommunityId ?? undefined;

  const [data, setData] = React.useState<CommunityCategoryDto>();
  const [drawerType, setDrawerType] = React.useState<GridActionType>(
    GRID_ACTION_TYPES.HIDDEN
  );
  const [showDialog, setShowDialog] = React.useState<boolean>(false);
  const [communityId, setCommunityId] = React.useState<string | undefined>(
    resolvedCommunityId
  );

  React.useEffect(() => {
    setCommunityId(resolvedCommunityId);
  }, [resolvedCommunityId]);

  const categoryQuery = CategoryService.getCategoryQuery(
    data?.id ?? Constants.emptyGuid
  );
  const lookupQuery = CategoryService.getLookups();
  const { mutateAsync: saveCommunitySection } = CategoryService.save();
  const { mutate: deleteMutate } = CategoryService.deleteCategory();

  const isEditMode = !!(data?.id && data.id !== Constants.emptyGuid);
  const closeDrawer = (): void => setDrawerType(GRID_ACTION_TYPES.HIDDEN);

  const refreshGrid = (): void => {
    isEditMode && categoryQuery.refetch();
    queryClient.invalidateQueries({
      queryKey: ["categoryGridQuery"],
    });
    closeDrawer();
  };

  const handleApiResponse = (response: any, successMessage: string): void => {
    const errorMsg = apiErrorMessage(response.errorMessages);
    if (errorMsg) {
      showSnackbar(errorMsg, MessageSeverity.Error);
    } else {
      showSnackbar(successMessage, MessageSeverity.Success);
      refreshGrid();
    }
  };

  const handleSubmit = async (values: CommunityCategoryDto) => {
    values.id = isEditMode ? data!.id : Constants.emptyGuid;
    const message = isEditMode ? "Category updated" : "Category saved";
    return await saveCommunitySection(values, {
      onSuccess: (response) => handleApiResponse(response, message),
    });
  };

  const handleCancelled = (): void => {
    deleteMutate(data?.id ?? Constants.emptyGuid, {
      onSuccess: (response) =>
        handleApiResponse(response, "Category cancelled"),
    });
  };

  const handleDelete = (): void => {
    deleteMutate(data?.id ?? Constants.emptyGuid, {
      onSuccess: (response) => handleApiResponse(response, "Category deleted"),
    });
  };

  const handleCustomEvent = (actionType: string, dataId: string): void => {
    if (actionType === GRID_ACTION_TYPES.DELETE) {
      setData({ id: dataId } as CommunityCategoryDto);
      setShowDialog(true);
      return;
    }

    if (isGridActionType(actionType)) {
      setDrawerType(actionType);
      setData({ id: dataId } as CommunityCategoryDto);
    }
  };

  const handleEdit = (): void => {
    setData(categoryQuery.data);
    setDrawerType(GRID_ACTION_TYPES.EDIT);
  };

  const renderDrawerContent = (): React.JSX.Element | null => {
    switch (drawerType) {
      case GRID_ACTION_TYPES.NEW:
        return (
          <CategoryForm
            show={true}
            onHide={closeDrawer}
            onSave={handleSubmit}
            lookups={lookupQuery?.data}
            communityId={communityId}
            disableCommunity={!!routeCommunityId || !!claimCommunityId}
          />
        );

      case GRID_ACTION_TYPES.EDIT:
        return (
          <CategoryForm
            data={categoryQuery.data}
            loading={categoryQuery.isLoading}
            show={true}
            onHide={closeDrawer}
            onSave={handleSubmit}
            lookups={lookupQuery?.data}
            communityId={communityId}
            disableCommunity={!!routeCommunityId || !!claimCommunityId}
          />
        );

      case GRID_ACTION_TYPES.VIEW:
      case GRID_ACTION_TYPES.DOUBLE_CLICK:
        return (
          <CategoryView
            data={categoryQuery?.data}
            show={true}
            onHide={closeDrawer}
            onCancel={handleCancelled}
            onEdit={handleEdit}
            onDelete={handleDelete}
            lookups={lookupQuery?.data}
          />
        );

      default:
        return null;
    }
  };
  if (lookupQuery.isLoading) {
    return <LoadingIndicator isFullPage />;
  }

  return (
    <>
      <CategoryPage
        onCustomEvent={handleCustomEvent}
        communityId={resolvedCommunityId}
        setCommunity={setCommunityId}
        hideTitle={!!routeCommunityId}
      />
      {renderDrawerContent()}
      <Dialog
        contentText={"Are you sure you want to delete?"}
        contentTitle={"Delete"}
        onClose={() => setShowDialog(false)}
        onConfirm={() => {
          setShowDialog(false);
          handleDelete();
        }}
        open={showDialog}
        closeText={"No, Go Back"}
        confirmText={"Yes, Continue"}
      />
    </>
  );
};
